import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ListLoadingState } from "@/components/loading/ListLoadingState";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { Clock, DollarSign, Calendar, CheckCircle2, AlertCircle, RefreshCw, Key, ShieldAlert } from "lucide-react";

interface CronJobMetadata {
  job_name?: string;
  error?: string;
  status?: string;
  timestamp?: string;
  [key: string]: any;
}

export const PayoutsAdmin = () => {
  const { toast } = useToast();
  const [isTriggering, setIsTriggering] = useState(false);
  const [serviceRoleKey, setServiceRoleKey] = useState("");
  const [isSavingKey, setIsSavingKey] = useState(false);

  const { data: keyConfigured, isLoading: isLoadingKeyStatus, refetch: refetchKeyStatus } = useQuery({
    queryKey: ['service-role-key-status'],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.rpc('check_service_role_key_configured');
        if (error) throw error;
        return !!data;
      } catch (err) {
        console.error("Error checking service role key status:", err);
        return false;
      }
    }
  });

  const { data: cronJobs, isLoading: isLoadingCron, refetch: refetchCronJobs } = useQuery({
    queryKey: ['admin-cron-jobs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_metrics')
        .select('*')
        .eq('metric_name', 'cron_job_attempt')
        .order('timestamp', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data;
    }
  });

  const { data: transactions, isLoading: isLoadingTransactions, refetch: refetchTransactions } = useQuery({
    queryKey: ['admin-payout-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('payout_status, chef_payout')
        .in('payout_status', ['pending', 'scheduled', 'completed'])
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      const stats = {
        pending: 0,
        scheduled: 0,
        completed: 0
      };

      data.forEach(tx => {
        stats[tx.payout_status] += parseFloat(tx.chef_payout.toString());
      });

      return stats;
    }
  });

  const { data: scheduledPayouts, isLoading: isLoadingScheduled, refetch: refetchScheduled } = useQuery({
    queryKey: ['admin-scheduled-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          id,
          chef_id,
          chef_payout,
          payout_scheduled_date,
          payout_status,
          chef:profiles!transactions_chef_id_fkey(first_name, last_name)
        `)
        .eq('payout_status', 'scheduled')
        .order('payout_scheduled_date', { ascending: true });
      
      if (error) throw error;
      return data;
    }
  });

  const { data: completedPayouts, isLoading: isLoadingCompleted, refetch: refetchCompleted } = useQuery({
    queryKey: ['admin-completed-payouts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          id,
          chef_id,
          chef_payout,
          payout_scheduled_date,
          payout_status,
          stripe_transfer_id,
          chef:profiles!transactions_chef_id_fkey(first_name, last_name)
        `)
        .eq('payout_status', 'completed')
        .not('stripe_transfer_id', 'is', null)
        .order('updated_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data;
    }
  });

  const triggerPayoutFunction = async (functionName: 'schedule-payouts' | 'process-scheduled-payouts') => {
    setIsTriggering(true);
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { manual: true },
      });

      if (error) throw error;

      toast({
        title: 'Function triggered successfully',
        description: `${functionName} has been manually triggered`,
        variant: 'default'
      });

      await Promise.all([
        refetchCronJobs(),
        refetchTransactions(),
        refetchScheduled(),
        refetchCompleted()
      ]);

    } catch (err) {
      console.error('Error triggering function:', err);
      toast({
        title: 'Error',
        description: `Failed to trigger ${functionName}: ${err.message}`,
        variant: 'destructive'
      });
    } finally {
      setIsTriggering(false);
    }
  };

  const saveServiceRoleKey = async () => {
    if (!serviceRoleKey.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a valid service role key',
        variant: 'destructive'
      });
      return;
    }

    setIsSavingKey(true);
    try {
      const { error } = await supabase.rpc('set_system_config', {
        config_name: 'service_role_key',
        config_value: serviceRoleKey.trim()
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Service role key has been saved',
        variant: 'default'
      });

      setServiceRoleKey('');
      refetchKeyStatus();

    } catch (err) {
      console.error('Error saving service role key:', err);
      toast({
        title: 'Error',
        description: `Failed to save service role key: ${err.message}`,
        variant: 'destructive'
      });
    } finally {
      setIsSavingKey(false);
    }
  };

  const refetchAll = async () => {
    await Promise.all([
      refetchCronJobs(),
      refetchTransactions(),
      refetchScheduled(),
      refetchCompleted(),
      refetchKeyStatus()
    ]);
    
    toast({
      title: 'Data refreshed',
      description: 'Payout data has been refreshed',
      variant: 'default'
    });
  };

  const getMetadataValue = (metadata: any, key: string, defaultValue: string = 'Unknown'): string => {
    if (!metadata) return defaultValue;
    
    if (typeof metadata === 'object' && metadata !== null) {
      return metadata[key] || defaultValue;
    }
    
    if (typeof metadata === 'string') {
      try {
        const parsed = JSON.parse(metadata);
        return parsed[key] || defaultValue;
      } catch (e) {
        return defaultValue;
      }
    }
    
    return defaultValue;
  };

  return (
    <AdminLayout title="Payouts Monitoring">
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-0">
          <h1 className="text-xl font-semibold">Payout System Monitoring</h1>
          <Button onClick={refetchAll} variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh Data
          </Button>
        </div>
        
        <Card className={keyConfigured ? "border-green-500/20 bg-green-50/50" : "border-yellow-500/20 bg-yellow-50/50"}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Key className={`h-4 w-4 ${keyConfigured ? "text-green-500" : "text-yellow-500"}`} />
              Service Role Key Configuration
            </CardTitle>
            <CardDescription>
              {keyConfigured 
                ? "The service role key is configured and ready for automated payouts" 
                : "Configure the service role key to enable automated payouts"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!keyConfigured && (
              <div className="space-y-4">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-muted-foreground">
                    The service role key is required for automated payout processing. 
                    This key is stored securely in the database and is only accessible by administrators.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="service-role-key">Service Role Key</Label>
                  <Input 
                    id="service-role-key" 
                    type="password" 
                    value={serviceRoleKey}
                    onChange={(e) => setServiceRoleKey(e.target.value)}
                    placeholder="Enter your Supabase service role key"
                    disabled={isSavingKey}
                  />
                </div>
                <Button 
                  onClick={saveServiceRoleKey}
                  disabled={isSavingKey || !serviceRoleKey.trim()}
                  className="flex items-center gap-2"
                >
                  {isSavingKey && <RefreshCw className="h-4 w-4 animate-spin" />}
                  Save Service Role Key
                </Button>
              </div>
            )}
            {keyConfigured && (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                <p>Service role key is properly configured and secure</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-yellow-500" />
                Pending Payouts
              </CardTitle>
              <CardDescription>Transactions awaiting scheduling</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {isLoadingTransactions ? "..." : `$${transactions?.pending.toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="h-4 w-4 text-blue-500" />
                Scheduled Payouts
              </CardTitle>
              <CardDescription>Ready for processing</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {isLoadingTransactions ? "..." : `$${transactions?.scheduled.toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
          
          <Card className="sm:col-span-2 md:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Completed Payouts
              </CardTitle>
              <CardDescription>Successfully processed</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {isLoadingTransactions ? "..." : `$${transactions?.completed.toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Manual Payout Actions</CardTitle>
            <CardDescription>Manually trigger payout functions (for testing or maintenance)</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={() => triggerPayoutFunction('schedule-payouts')}
              disabled={isTriggering || !keyConfigured}
              className="flex items-center gap-2"
            >
              <Calendar className="h-4 w-4" />
              Schedule Payouts
            </Button>
            <Button 
              onClick={() => triggerPayoutFunction('process-scheduled-payouts')}
              disabled={isTriggering || !keyConfigured}
              className="flex items-center gap-2"
            >
              <DollarSign className="h-4 w-4" />
              Process Scheduled Payouts
            </Button>
          </CardContent>
          {!keyConfigured && (
            <CardFooter className="pt-0">
              <p className="text-sm text-muted-foreground">
                Manual actions are disabled until the service role key is configured
              </p>
            </CardFooter>
          )}
        </Card>

        <Tabs defaultValue="scheduled" className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-md mx-auto">
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="completed">Completed</TabsTrigger>
            <TabsTrigger value="jobs">Cron Jobs</TabsTrigger>
          </TabsList>

          <TabsContent value="scheduled" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Payouts</CardTitle>
                <CardDescription>Payments scheduled to be processed</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingScheduled ? (
                  <ListLoadingState />
                ) : scheduledPayouts?.length ? (
                  <div className="space-y-4">
                    {scheduledPayouts.map(payout => (
                      <Card key={payout.id} className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">Chef</p>
                            <p className="font-medium">{payout.chef?.first_name} {payout.chef?.last_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Amount</p>
                            <p className="font-medium">${parseFloat(payout.chef_payout.toString()).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Scheduled Date</p>
                            <p className="font-medium">{format(new Date(payout.payout_scheduled_date), 'MMM d, yyyy')}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Status</p>
                            <p className="font-medium capitalize">{payout.payout_status}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No scheduled payouts found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="completed" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Completed Payouts</CardTitle>
                <CardDescription>Recently processed payments</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingCompleted ? (
                  <ListLoadingState />
                ) : completedPayouts?.length ? (
                  <div className="space-y-4">
                    {completedPayouts.map(payout => (
                      <Card key={payout.id} className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">Chef</p>
                            <p className="font-medium">{payout.chef?.first_name} {payout.chef?.last_name}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Amount</p>
                            <p className="font-medium">${parseFloat(payout.chef_payout.toString()).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Transfer ID</p>
                            <p className="font-medium text-xs truncate">{payout.stripe_transfer_id}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Status</p>
                            <p className="font-medium capitalize text-green-600">{payout.payout_status}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No completed payouts found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="jobs" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Cron Job History</CardTitle>
                <CardDescription>History of automated payout runs</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingCron ? (
                  <ListLoadingState />
                ) : cronJobs?.length ? (
                  <div className="space-y-4">
                    {cronJobs.map(job => (
                      <Card key={job.id} className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-muted-foreground text-xs">Job Name</p>
                            <p className="font-medium">{getMetadataValue(job.metadata, 'job_name', 'Unknown')}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Status</p>
                            <p className={`font-medium ${job.metric_value === 1 ? 'text-green-600' : 'text-red-600'}`}>
                              {job.metric_value === 1 ? 'Success' : 'Failed'}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Timestamp</p>
                            <p className="font-medium">{format(new Date(job.timestamp), 'MMM d, yyyy HH:mm:ss')}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground text-xs">Error</p>
                            <p className="font-medium text-xs truncate">{getMetadataValue(job.metadata, 'error', 'None')}</p>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No cron job history found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default PayoutsAdmin;
